import { ArrowRight, Filter, Play, Search } from "lucide-react";
import { useMemo, useState } from "react";
import type { PublicChallenge } from "../types";

function VideoPreview({ challenge }: { challenge: PublicChallenge }) {
  const [playing, setPlaying] = useState(false);
  const thumbnail = challenge.previewUrl || `https://i.ytimg.com/vi/${challenge.youtubeVideoId}/hqdefault.jpg`;
  if (playing) {
    return (
      <iframe
        className="challenge-video"
        src={`https://www.youtube-nocookie.com/embed/${challenge.youtubeVideoId}?autoplay=1`}
        title={`Lösungsvideo: ${challenge.title}`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }
  return (
    <button className="video-poster" type="button" onClick={() => setPlaying(true)} aria-label={`Video ${challenge.title} abspielen`}>
      <img src={thumbnail} alt="" loading="lazy" />
      <span><Play fill="currentColor" /></span>
      <small>Video laden</small>
    </button>
  );
}

export function ChallengeGallery({ challenges }: { challenges: PublicChallenge[] }) {
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState("");
  const [grade, setGrade] = useState("all");

  const filtered = useMemo(() => challenges.filter((challenge) => {
    const matchesGrade = grade === "all" || challenge.grade === grade;
    const haystack = `${challenge.code} ${challenge.title} ${challenge.topic}`.toLowerCase();
    return matchesGrade && haystack.includes(query.toLowerCase());
  }), [challenges, grade, query]);

  const visibleChallenges = showAll ? filtered : challenges.slice(0, 3);

  return (
    <section className="gallery-section section-pad" id="loesungen" aria-labelledby="challenge-gallery-title">
      <div className="section-heading challenge-heading">
        <div>
          <div className="eyebrow">So erkläre ich Mathe</div>
          <h2 id="challenge-gallery-title">Echte Aufgaben. Verständliche Wege.</h2>
          <p>Schau dir an, wie ich schwierige Aufgaben Schritt für Schritt nachvollziehbar mache.</p>
        </div>
        {showAll && challenges.length > 0 && (
          <div className="gallery-filters">
            <label><Search /><input aria-label="Challenges durchsuchen" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Thema oder Code" /></label>
            <label><Filter /><select aria-label="Nach Klasse filtern" value={grade} onChange={(event) => setGrade(event.target.value)}><option value="all">Alle Klassen</option>{[6, 7, 8, 9, 10, 11, 12, 13].map((item) => <option key={item}>Klasse {item}</option>)}<option>Abitur</option></select></label>
          </div>
        )}
      </div>

      {visibleChallenges.length ? (
        <div className="gallery-grid">
          {visibleChallenges.map((challenge) => (
            <article className="challenge-card" key={challenge.id}>
              <VideoPreview challenge={challenge} />
              <div className="challenge-card-body">
                <div className="challenge-meta"><span>{challenge.grade}</span><span>{challenge.topic}</span></div>
                <h3>{challenge.title}</h3>
                <p>{challenge.teaser}</p>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="gallery-empty"><span>∑</span><h3>{challenges.length ? "Keine passende Erklärung gefunden" : "Die ersten Erklärungen entstehen gerade"}</h3><p>{challenges.length ? "Probiere einen anderen Suchbegriff oder Filter." : "Hier erscheinen nur echte, freigegebene Lösungsvideos."}</p></div>
      )}

      {challenges.length > 3 && (
        <button className="button button-secondary centered-button" type="button" onClick={() => { setShowAll((current) => !current); setQuery(""); setGrade("all"); }} aria-expanded={showAll}>
          {showAll ? "Weniger anzeigen" : "Alle Erklärungen ansehen"} <ArrowRight />
        </button>
      )}
    </section>
  );
}
